/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 22/05/2022
 */

import * as React from 'react';
import { LabelCell, Row, Table, ValueCell } from '@/wpt-lib/pdf/common/Table';
import useData from '@/wpt-lib/pdf/useData';
import Page from '@/wpt-lib/pdf/common/Page';
import Heading from '@/wpt-lib/pdf/common/Heading';
import { WorkloadStaffType, WorkplanState } from '@/wpt-lib/interfaces/Workplan';
import { formatName } from '@/wpt-lib/utils/formatName';
import { WorkplanCommentSection } from '@/wpt-lib/interfaces/WorkplanComment';
import SubHeading from '@/wpt-lib/pdf/common/SubHeading';
import { RatingsDisplay } from '@/wpt-lib/pdf/review/RatingsDisplay';

const Ratings = ['Below', 'Partially Met', 'Met', 'Exceeding', 'Highly Exceeding'];

export const CategoryRating = ({
    label,
    rating,
    hasHighlyExceeding,
}: {
    label: string;
    rating?: number;
    hasHighlyExceeding: boolean;
}) => {
    const ratings = hasHighlyExceeding ? Ratings : Ratings.slice(0, 4);
    return (
        <Row border>
            <LabelCell label={label + ':'} />
            {ratings.map((r, i) => (
                <ValueCell
                    textAlign="center"
                    key={i}
                    label={r}
                    fontSize={13}
                    value={i + 1 === rating ? '\u00d7' : ''}
                />
            ))}
        </Row>
    );
};

const Rating = () => {
    const { version, stateHistory, comments } = useData();

    const state = stateHistory
        .filter((state) => state.version <= version.version)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .find((h) => h.to === WorkplanState.ENDORSED);

    const eoyComment = comments.find((f) => f.section === WorkplanCommentSection.END_YEAR);

    const isEOYRatingGiven = (): boolean => {
        const {
            overallEndOfYearRating,
            teachingEndOfYearRating,
            researchEndOfYearRating,
            serviceEndOfYearRating,
            staffType,
        } = version;

        switch (staffType) {
            case WorkloadStaffType.PROFESSIONAL:
                return !!overallEndOfYearRating;

            case WorkloadStaffType.SENIOR:
                return !!overallEndOfYearRating;

            case WorkloadStaffType.ACADEMIC:
                return !!(
                    overallEndOfYearRating &&
                    teachingEndOfYearRating &&
                    researchEndOfYearRating &&
                    serviceEndOfYearRating
                );

            default:
                return false;
        }
    };

    const hasHighlyExceeding =
        !!isEOYRatingGiven &&
        Math.max(
            version.overallEndOfYearRating ?? 0,
            version.teachingEndOfYearRating ?? 0,
            version.researchEndOfYearRating ?? 0,
            version.serviceEndOfYearRating ?? 0,
        ) === 5;

    return (
        <Page>
            <Heading text="End of Year Rating" />

            {eoyComment && (
                <Table cols={3}>
                    <Row>
                        <SubHeading level={2} text="End of year comment" />
                    </Row>
                    <Row widths={[15, 15, 70]}>
                        <ValueCell label="Date" value={eoyComment.createdAt} format="date" />
                        <ValueCell label="Author" value={formatName(eoyComment.author?.user)} />
                        <ValueCell label="Comment" value={eoyComment.comment} />
                    </Row>
                </Table>
            )}

            <Table cols={6}>
                <Row widths={[20, 80]}>
                    <ValueCell label="Endorsed Date" value={state?.createdAt} format="date" />
                    <ValueCell label="Endorsed By" value={state ? formatName(state?.assignment.user) : undefined} />
                </Row>
                <RatingsDisplay version={version} hasHighlyExceeding={hasHighlyExceeding} />
                <Row>
                    <LabelCell label="" />
                </Row>
                <CategoryRating
                    label="Overall"
                    rating={version.overallEndOfYearRating ? version.overallEndOfYearRating : undefined}
                    hasHighlyExceeding={hasHighlyExceeding}
                />
            </Table>
        </Page>
    );
};

export default Rating;
